import React, { useState } from "react";

// Import centralized utilities
import { SAP_PRESETS } from "./utils/sapValues";
import { OIL_GROUPS, LATHER_TYPES, TRACE_SPEEDS } from "./utils/validationRules";

const CATEGORIES = ["Oil", "Soap Base", "Additive", "Fragrance", "Colour", "Alkali"];
const ADDITIVE_TYPES = ["Clay", "Herb", "Scrub", "Liquid", "Functional"];
const SUITABILITY_OPTIONS = ["All", "Dry", "Sensitive", "Acne-prone", "Oily"];
const SCALE_OPTIONS = ["Low", "Medium", "High"];

export default function AddIngredient({ onAdd }) {
  const [category, setCategory] = useState("Oil");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [form, setForm] = useState({
    name: "",
    cost: "",
    unit: "kg",
    stock: "",
    densityGPerMl: "",
    sap: "",
    lather: "Medium",
    hardness: "Medium",
    faceSafe: true,
    type: "oil",
    recommendedMaxPct: 70,
    faceMaxPct: 100,
    absoluteMaxPct: 100,
    oilGroup: "conditioning",
    latherType: "creamy",
    traceSpeed: "medium",
    subType: "Clay",
    colour: "",
    maxFacePct: "",
    maxBodyPct: "",
    skinSuitability: [],
    expiryDate: "", // New: track ingredient expiry
    purchaseDate: "", // New: track purchase date
    supplier: "", // New: track supplier
  });

  function getInitialForm(cat) {
    return {
      name: "",
      cost: "",
      unit: "kg",
      stock: "",
    densityGPerMl: "",
      sap: "",
      lather: "Medium",
      hardness: "Medium",
      faceSafe: true,
      expiryDate: "",
      purchaseDate: "",
      supplier: "",
      recommendedMaxPct: 70,
      faceMaxPct: 100,
      absoluteMaxPct: 100,
      oilGroup: "conditioning",
      latherType: "creamy",
      traceSpeed: "medium",
      subType: "Clay",
      colour: "",
      maxFacePct: "",
      maxBodyPct: "",
      skinSuitability: [],
    };
  }

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSuitability = (type) => {
    setForm((prev) => {
      const current = prev.skinSuitability || [];
      if (current.includes(type)) {
        return { ...prev, skinSuitability: current.filter((t) => t !== type) };
      }
      return { ...prev, skinSuitability: [...current, type] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || saving) return;
    setSaveError('');
    if ([form.cost || 0, form.stock || 0].some((value) => !Number.isFinite(Number(value)) || Number(value) < 0)) { setSaveError('Cost and stock must be non-negative numbers.'); return; }
    if (form.densityGPerMl !== '' && !(Number(form.densityGPerMl) > 0)) { setSaveError('Density must be a positive number in g/ml.'); return; }
    // A SAP in the wrong unit (e.g. 190 mg KOH/g) would multiply the lye several-fold.
    if (category === 'Oil' && !(Number(form.sap) >= 0.05 && Number(form.sap) <= 0.3)) { setSaveError('Enter the NaOH SAP value as a decimal between about 0.06 and 0.28 (olive oil is 0.134). If your supplier gives mg KOH/g, such as 190, divide by 1403.'); return; }

    const base = {
      category: category.toLowerCase(),
      key: form.name.toLowerCase().replace(/\s+/g, "_"),
      name: form.name,
      costPerUnit: Number(form.cost) || 0,
      unit: form.unit,
      stock: Number(form.stock) || 0,
      densityGPerMl: form.densityGPerMl === '' ? null : Number(form.densityGPerMl),
      // New: expiry and purchase tracking
      expiryDate: form.expiryDate || null,
      purchaseDate: form.purchaseDate || null,
      supplier: form.supplier || null,
    };

    let result = { ...base };

    if (category === "Oil") {
      result = {
        ...result,
        sap: Number(form.sap) || 0,
        properties: {
          lather: form.lather,
          hardness: form.hardness,
        },
        faceSafe: form.faceSafe,
        recommendedMaxPct: Number(form.recommendedMaxPct) || 70,
        faceMaxPct: Number(form.faceMaxPct) || 100,
        absoluteMaxPct: Number(form.absoluteMaxPct) || 100,
        oilGroup: form.oilGroup,
        latherType: form.latherType,
        traceSpeed: form.traceSpeed,
      };
    } else if (category === "Additive") {
      result = {
        ...result,
        type: form.subType,
        skin: form.skinSuitability.length ? form.skinSuitability : ["All"],
        colour: form.colour,
        faceSafe: form.faceSafe,
      };
    } else if (category === "Fragrance") {
      result = {
        ...result,
        maxFacePct: Number(form.maxFacePct) || 0,
        maxBodyPct: Number(form.maxBodyPct) || 0,
        skin: form.skinSuitability.length ? form.skinSuitability : ["All"],
      };
    } else if (category === "Colour") {
      result = {
        ...result,
        sources: [form.name],
        faceSafe: form.faceSafe,
      };
    } else if (category === "Alkali") {
      result = {
        ...result,
        maxFacePct: Number(form.maxFacePct) || 0,
        maxBodyPct: Number(form.maxBodyPct) || 0,
        skin: form.skinSuitability.length ? form.skinSuitability : ["All"],
      };
    }

    setSaving(true);
    try { await onAdd(result); setForm(getInitialForm(category)); }
    catch (error) { setSaveError(error.message || 'The ingredient could not be saved. Your entries have been kept.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="ing-form-wrapper">
      <h3>Add New Component</h3>
      
      <div className="ing-type-select">
        <label>Type:</label>
        <select 
          value={category} 
          onChange={(e) => {
            setCategory(e.target.value);
            setForm(getInitialForm(e.target.value));
          }}
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <form aria-label="Add ingredient" onSubmit={handleSubmit} className="ing-form">
        <div className="ing-form-row three-col">
          <label>
            <div className="field-label">Name</div>
            <input 
              placeholder="e.g. Olive Oil"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
            />
          </label>
          <label>
            <div className="field-label">Cost</div>
            <input 
              type="number" 
              placeholder="0.00"
              value={form.cost}
              onChange={(e) => handleChange("cost", e.target.value)}
            />
          </label>
          <div>
            <label>
              <div className="field-label">Unit</div>
              <select 
                value={form.unit}
                onChange={(e) => handleChange("unit", e.target.value)}
              >
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="l">l</option>
                <option value="ml">ml</option>
              </select>
            </label>
            <div style={{ fontSize: "0.75em", color: "#64748b", marginTop: 4 }}>
              (per unit of {form.unit})
            </div>
          </div>
        </div>

        {['l', 'ml'].includes(form.unit.toLowerCase()) && <label><div className="field-label">Density (g/ml, optional)</div><input type="number" min="0.001" step="any" value={form.densityGPerMl} onChange={(event) => handleChange('densityGPerMl', event.target.value)} /><small>Use your supplier's measured density to convert volume stock to recipe weight.</small></label>}
        <div className="ing-properties-box">
          <div className="box-title">
            {category} Properties
          </div>
          
          {category === "Soap Base" && (
            <small>A premade melt-and-pour base (for example clear glycerin, goat milk or shea). Use it in the Melt &amp; Pour calculator; batches deduct it from this stock.</small>
          )}

          {category === "Oil" && (
            <div style={{ display: "grid", gap: 12 }}>
              {/* SAP Preset Selector */}
              <div className="ing-preset-box">
                <div className="preset-title">
                  Quick Select from Standard Oils/Butters
                </div>
                <select 
                  value=""
                  onChange={(e) => {
                    const preset = SAP_PRESETS.find(p => p.name === e.target.value);
                    if (preset) {
                      setForm(prev => ({
                        ...prev,
                        name: preset.name,
                        sap: preset.sap,
                        oilGroup: preset.oilGroup,
                        latherType: preset.latherType,
                        traceSpeed: preset.traceSpeed,
                        hardness: preset.hardness,
                        recommendedMaxPct: preset.recommendedMaxPct,
                        absoluteMaxPct: preset.absoluteMaxPct,
                        faceMaxPct: preset.absoluteMaxPct,
                      }));
                    }
                  }}
                >
                  <option value="">-- Select a standard oil/butter --</option>
                  <optgroup label="Common Oils">
                    {SAP_PRESETS.filter(p => p.oilGroup !== 'butter').map(p => (
                      <option key={p.name} value={p.name}>{p.name} (SAP: {p.sap})</option>
                    ))}
                  </optgroup>
                  <optgroup label="Butters">
                    {SAP_PRESETS.filter(p => p.oilGroup === 'butter').map(p => (
                      <option key={p.name} value={p.name}>{p.name} (SAP: {p.sap})</option>
                    ))}
                  </optgroup>
                </select>
                <div className="preset-hint">
                  Select to auto-fill name, SAP, and recommended properties
                </div>
              </div>

              <div className="ing-form-row two-col">
                <label>
                  <div className="field-label">SAP Value (NaOH)</div>
                  <input type="number" step="0.001" min="0.05" max="0.3" value={form.sap} onChange={e => handleChange("sap", e.target.value)} required placeholder="0.134" />
                  <small>Grams of NaOH per gram of oil, e.g. 0.134 for olive oil. Pick the oil above to fill it in. A wrong SAP value gives a wrong lye amount.</small>
                </label>
              </div>
              <div className="ing-form-row three-equal">
                <label>
                  <div className="field-label">Recommended Max %</div>
                  <input type="number" value={form.recommendedMaxPct} onChange={e => handleChange("recommendedMaxPct", e.target.value)} placeholder="70" />
                </label>
                <label>
                  <div className="field-label">Face Max %</div>
                  <input type="number" value={form.faceMaxPct} onChange={e => handleChange("faceMaxPct", e.target.value)} placeholder="100" />
                </label>
                <label>
                  <div className="field-label">Absolute Max %</div>
                  <input type="number" value={form.absoluteMaxPct} onChange={e => handleChange("absoluteMaxPct", e.target.value)} placeholder="100" />
                </label>
              </div>
              <div className="ing-form-row four-col">
                <label>
                  <div className="field-label">Hardness</div>
                  <select value={form.hardness} onChange={e => handleChange("hardness", e.target.value)}>
                    {SCALE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
                <label>
                  <div className="field-label">Oil Group</div>
                  <select value={form.oilGroup} onChange={e => handleChange("oilGroup", e.target.value)}>
                    {OIL_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </label>
                <label>
                  <div className="field-label">Lather Type</div>
                  <select value={form.latherType} onChange={e => handleChange("latherType", e.target.value)}>
                    {LATHER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label>
                  <div className="field-label">Trace Speed</div>
                  <select value={form.traceSpeed} onChange={e => handleChange("traceSpeed", e.target.value)}>
                    {TRACE_SPEEDS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
              <label className="ing-checkbox-row">
                <input type="checkbox" checked={form.faceSafe} onChange={e => handleChange("faceSafe", e.target.checked)} />
                Safe for Face Use?
              </label>
            </div>
          )}

          {category === "Additive" && (
            <div style={{ display: "grid", gap: 8 }}>
               <label>
                  <div className="field-label">Additive Type</div>
                  <select value={form.subType} onChange={e => handleChange("subType", e.target.value)}>
                    {ADDITIVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
               </label>
               <label>
                  <div className="field-label">Imparts Colour? (Optional)</div>
                  <input type="text" placeholder="e.g. Pink" value={form.colour} onChange={e => handleChange("colour", e.target.value)} />
               </label>
               <label className="ing-checkbox-row">
                <input type="checkbox" checked={form.faceSafe} onChange={e => handleChange("faceSafe", e.target.checked)} />
                Safe for Face Use?
              </label>
            </div>
          )}

          {category === "Fragrance" && (
             <div className="ing-form-row two-col">
                <label>
                  <div className="field-label">Max Face %</div>
                  <input type="number" step="0.1" value={form.maxFacePct} onChange={e => handleChange("maxFacePct", e.target.value)} />
                </label>
                <label>
                  <div className="field-label">Max Body %</div>
                  <input type="number" step="0.1" value={form.maxBodyPct} onChange={e => handleChange("maxBodyPct", e.target.value)} />
                </label>
             </div>
          )}

          {(category === "Additive" || category === "Fragrance") && (
            <div className="ing-suitability">
              <div className="suit-label">Skin Suitability:</div>
              <div className="suit-pills">
                {SUITABILITY_OPTIONS.map(opt => (
                  <label key={opt} className="suit-pill">
                    <input 
                      type="checkbox" 
                      checked={(form.skinSuitability || []).includes(opt)}
                      onChange={() => toggleSuitability(opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
          )}

          {category === "Colour" && (
            <div style={{ display: "grid", gap: 8 }}>
               <label className="ing-checkbox-row">
                <input type="checkbox" checked={form.faceSafe} onChange={e => handleChange("faceSafe", e.target.checked)} />
                Safe for Face Use?
              </label>
            </div>
          )}
        </div>

        {/* Stock, Expiry, Purchase & Supplier Section */}
        <div className="ing-stock-box">
          <div className="box-title">
            Stock & Tracking
          </div>
          <div className="ing-form-row four-col">
            <label>
              <div className="field-label">Stock Available</div>
              <input 
                type="number"
                placeholder="0"
                value={form.stock}
                onChange={(e) => handleChange("stock", e.target.value)}
              />
            </label>
            <label>
              <div className="field-label">Purchase Date</div>
              <input 
                type="date"
                value={form.purchaseDate}
                onChange={(e) => handleChange("purchaseDate", e.target.value)}
              />
            </label>
            <label>
              <div className="field-label">Expiry Date</div>
              <input 
                type="date"
                value={form.expiryDate}
                onChange={(e) => handleChange("expiryDate", e.target.value)}
              />
            </label>
            <label>
              <div className="field-label">Supplier</div>
              <input 
                type="text"
                placeholder="Supplier name"
                value={form.supplier}
                onChange={(e) => handleChange("supplier", e.target.value)}
              />
            </label>
          </div>
        </div>

        {saveError && <p role="alert">{saveError}</p>}
        <button type="submit" className="ing-submit-btn" disabled={saving}>
          {saving ? 'Saving…' : `Add ${category} To Inventory`}
        </button>
      </form>
    </div>
  );
}
